import React from 'react';
import { useTranslation } from 'react-i18next';
import { FinalCompetitionConfiguration, FinalCompetitionPhase, LaneData } from '../models/Models';
import FinalCompetitionTarget from './FinalCompetitionTarget';
import WeatherLowerThird from './WeatherLowerThird';
import { Translation } from '../helper/WinfireLiveConstHelper';
import './FinalCompetition.css';

interface FinalCompetitionViewProps {
    header: string;
    laneData: LaneData[] | undefined | null;
    finalCompetitionConfiguration: FinalCompetitionConfiguration | undefined;
}

/**
 * ISSF Elimination Final screen.
 *
 * Layout:
 *   - Left (flex:1): X × Y CSS-grid of FinalCompetitionTarget tiles — full height to navbar
 *   - Right (fixed ~17%): Program name → Phase label → Rank table (if showRankTable)
 *
 * The top header row has been removed so the target grid gains the full available height.
 * Dense rank is computed here from currentTotal (descending).
 * Only lanes with shots receive a rank; tied totals get the same rank.
 * Grid cells are filled in ascending laneNumber order (dense, no gaps) so that e.g.
 * lanes 3–12, 1–8, or 1–5 + 8–11 always fill consecutive grid positions left-to-right / top-to-bottom.
 */
function FinalCompetitionView(props: FinalCompetitionViewProps) {
    const { t } = useTranslation();
    const config = props.finalCompetitionConfiguration;

    if (!config) {
        return (
            <div>
                <h1>{props.header}</h1>
            </div>
        );
    }

    // Capture as a non-nullable const so TypeScript narrows the type inside nested functions.
    const cfg: FinalCompetitionConfiguration = config;

    const xCount = cfg.xLaneCount ?? 4;
    const yCount = cfg.yLaneCount ?? 2;
    const numberOfCompetitionShots = cfg.numberOfCompetitionShots ?? 10;
    const showRankTable = cfg.showRankTable ?? false;

    // Show the weather lower third only during the Preparation (Probeschüsse) phase.
    const showLowerThird = cfg.currentFinalPhase === FinalCompetitionPhase.Value1;

    // Map phase enum value to translation key
    // FinalCompetitionPhase: Value0=Registration, Value1=Preparation, Value2=CompetitionShots, Value3=Elimination, Value4=Finished
    const phaseTranslationKey = (() => {
        switch (cfg.currentFinalPhase) {
            case FinalCompetitionPhase.Value0:  return Translation.PHASE_REGISTRATION;
            case FinalCompetitionPhase.Value1:  return Translation.PHASE_PREPARATION;
            case FinalCompetitionPhase.Value2:  return Translation.PHASE_COMPETITION_SHOTS;
            case FinalCompetitionPhase.Value3:  return Translation.PHASE_ELIMINATION;
            case FinalCompetitionPhase.Value4:  return Translation.PHASE_FINISHED;
            default:                            return Translation.PHASE_REGISTRATION;
        }
    })();

    const laneData = props.laneData ?? [];

    // Sort lanes by laneNumber ascending and skip completely empty lanes (no participant, no shots).
    // This way the grid is always filled densely with only active/registered lanes,
    // regardless of which lane numbers are used (e.g. 3–12, 1–8, 1–5+8–11).
    const sortedLanes = [...laneData]
        .filter(ld => !!(ld.participantName) || (ld.shots?.length ?? 0) > 0)
        .sort((a, b) => (a.laneNumber ?? 0) - (b.laneNumber ?? 0));

    // ── Issue 2: Dense rank computation (client-side) ─────────────────────────
    // Only lanes with at least one shot are ranked (avoids ranking empty lanes even if total=0).
    // Ties → same rank.
    const sorted = [...laneData]
        .filter(ld => (ld.shots?.length ?? 0) > 0)
        .sort((a, b) => (b.currentTotal ?? 0) - (a.currentTotal ?? 0));
    let currentRank = 0;
    let prevTotal: number | undefined = undefined;
    const rankMap = new Map<number, number>(); // laneNumber → rank (F-06: no undefined key)

    for (const ld of sorted) {
        if (ld.currentTotal !== prevTotal) {
            currentRank++;
            prevTotal = ld.currentTotal;
        }
        // F-06: Only insert into map when laneNumber is defined to avoid undefined-key collisions
        if (ld.laneNumber !== undefined) {
            rankMap.set(ld.laneNumber, currentRank);
        }
    }

    // Tenth-scoring detection: check all shots across all lanes for any non-integer value.
    // Mirrors the same logic in FinalCompetitionTarget so formatting is consistent.
    const isTenthScoring = laneData.some(ld =>
        ld.shots?.some(s => s.value != null && !Number.isInteger(s.value)) ?? false
    );
    const formatTotal = (v: number | null | undefined): string => {
        if (v == null) return '';
        return isTenthScoring ? v.toFixed(1) : (Number.isInteger(v) ? String(v) : v.toFixed(1));
    };

    // Grid: fill tiles in ascending laneNumber order (dense — no gap for skipped lane numbers).
    // Extra tiles beyond the number of available lanes are rendered as empty.
    function renderGrid() {
        return Array.from({ length: xCount * yCount }, (_, tileIndex) => {
            const ld = sortedLanes[tileIndex]; // undefined when fewer lanes than tiles
            const rank = ld?.laneNumber !== undefined
                ? (rankMap.get(ld.laneNumber) ?? 0)
                : 0;
            return (
                <FinalCompetitionTarget
                    key={tileIndex}
                    laneData={ld}
                    numberOfCompetitionShots={numberOfCompetitionShots}
                    displayTargetType={cfg.displayTargetType}
                    currentFinalPhase={cfg.currentFinalPhase}
                    rank={rank}
                />
            );
        });
    }

    // Rank table rows — sorted by dense rank ascending; eliminated athletes pinned to the bottom.
    // Filter out lanes that have neither a participant name nor any shots (empty/unused lanes)
    // to avoid showing "0.0" entries for unregistered slots.
    const rankTableRows = [...laneData]
        .filter(ld => !!(ld.participantName) || (ld.shots?.length ?? 0) > 0)
        .sort((a, b) => {
        const elimDiff = (a.isEliminated ? 1 : 0) - (b.isEliminated ? 1 : 0);
        if (elimDiff !== 0) return elimDiff;
        const ra = a.laneNumber !== undefined ? (rankMap.get(a.laneNumber) ?? 9999) : 9999;
        const rb = b.laneNumber !== undefined ? (rankMap.get(b.laneNumber) ?? 9999) : 9999;
        return ra - rb;
    });

    return (
        // Outer flex — fills height given by MainStage (100vh minus TopLive navbar)
        <div className="fc-view">

            {/* ── Left: target grid ── */}
            <div
                className="fc-grid"
                style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${xCount}, 1fr)`,
                    gridTemplateRows: `repeat(${yCount}, 1fr)`,
                }}
            >
                {renderGrid()}
            </div>

            {/* ── Right panel: Program name → Phase → Rank table ── */}
            <div className="fc-sidebar">
                {/* Program name */}
                <div className="fc-program-name" title={props.header}>
                    {props.header}
                </div>

                {/* Phase label */}
                <div className="fc-phase-label">
                    {t(phaseTranslationKey)}
                </div>

                {/* Rank table — fills remaining height with scroll */}
                {showRankTable && (
                    <div className="fc-rank-scroll">
                        <table className="fc-rank-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '16%' }}>#</th>
                                    <th style={{ width: '28%', textAlign: 'right' }}>{t(Translation.CURRENT_TOTAL_SHORT)}</th>
                                    <th style={{ width: '56%' }}>{t(Translation.SHOOTER)}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rankTableRows.map((ld, i) => {
                                    const rowRank = ld.laneNumber !== undefined ? (rankMap.get(ld.laneNumber) ?? 0) : 0;
                                    const rankClass = rowRank === 1 ? 'fc-rank-1' : rowRank === 2 ? 'fc-rank-2' : rowRank === 3 ? 'fc-rank-3' : '';
                                    return (
                                        <tr
                                            key={ld.laneNumber ?? i}
                                            className={`${rankClass}${ld.isEliminated ? ' fc-eliminated' : ''}`}
                                        >
                                            <td className="fc-rank-cell">
                                                {rowRank > 0 ? rowRank : ''}
                                            </td>
                                            <td className="fc-total-cell">
                                                {formatTotal(ld.currentTotal)}
                                            </td>
                                            <td
                                                className="fc-name-cell"
                                                title={ld.participantName ?? undefined}
                                            >
                                                {ld.participantName ?? ''}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* ── Weather lower third — slides in during Preparation (Probeschüsse) phase ── */}
                <WeatherLowerThird
                    locationName={cfg.locationName}
                    weatherData={cfg.weather}
                    visible={showLowerThird}
                />
            </div>
        </div>
    );
}

export default FinalCompetitionView;

